import { mountRetroArcade, type RetroArcadeEmbedHandle } from './sdk';

export const retroArcadePlayerTagName = 'retro-arcade-player' as const;

export interface RetroArcadePlayerElement extends HTMLElement {
  loadGame(game: string): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  restart(): Promise<void>;
  setVolume(volume: number): Promise<void>;
  requestGameFullscreen(): Promise<void>;
}

declare global {
  interface HTMLElementTagNameMap {
    'retro-arcade-player': RetroArcadePlayerElement;
  }
}

function requiredPlayerOrigin(element: HTMLElement): string {
  const origin = element.getAttribute('player-origin')?.trim();
  if (!origin) throw new Error('<retro-arcade-player> requires a player-origin attribute.');
  return origin;
}

export function defineRetroArcadePlayerElement(
  registry: CustomElementRegistry = window.customElements,
): void {
  if (registry.get(retroArcadePlayerTagName)) return;

  class RetroArcadePlayerElementImplementation
    extends HTMLElement
    implements RetroArcadePlayerElement
  {
    static readonly observedAttributes = ['player-origin', 'game'];

    private handle: RetroArcadeEmbedHandle | null = null;
    private unsubscribeEvents: Array<() => void> = [];

    connectedCallback() {
      if (this.handle) return;

      let playerOrigin: string;
      try {
        playerOrigin = requiredPlayerOrigin(this);
      } catch (error) {
        this.reportError(error);
        return;
      }

      const root = this.shadowRoot ?? this.attachShadow({ mode: 'open' });
      root.replaceChildren();

      const style = this.ownerDocument.createElement('style');
      style.textContent = `
        :host {
          display: block;
          width: 100%;
          min-height: 32rem;
          contain: content;
        }

        .retro-arcade-element-mount {
          width: 100%;
          height: 100%;
          min-height: inherit;
        }
      `;
      const mount = this.ownerDocument.createElement('div');
      mount.className = 'retro-arcade-element-mount';
      root.append(style, mount);

      try {
        this.handle = mountRetroArcade(mount, {
          playerOrigin,
          title: this.getAttribute('title')?.trim() || 'Retro Arcade Player',
        });
      } catch (error) {
        this.reportError(error);
        return;
      }

      this.unsubscribeEvents = [
        this.handle.on('PLAYER_READY', (detail) => this.dispatchRetroEvent('ready', detail)),
        this.handle.on('GAME_CHANGED', (detail) => this.dispatchRetroEvent('game-changed', detail)),
        this.handle.on('RUNTIME_STATE', (detail) =>
          this.dispatchRetroEvent('runtime-state', detail),
        ),
        this.handle.on('PLAYER_ERROR', (detail) => this.dispatchRetroEvent('error', detail)),
      ];

      void this.handle.ready
        .then(() => {
          const game = this.getAttribute('game')?.trim();
          if (game) return this.handle?.loadGame(game);
          return undefined;
        })
        .catch((error) => this.reportError(error));
    }

    disconnectedCallback() {
      this.teardown();
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
      if (oldValue === newValue || !this.isConnected) return;

      if (name === 'player-origin') {
        this.teardown();
        this.connectedCallback();
        return;
      }

      if (name === 'game' && this.handle && newValue?.trim()) {
        void this.handle.loadGame(newValue.trim()).catch((error) => this.reportError(error));
      }
    }

    loadGame(game: string) {
      return this.requireHandle().loadGame(game);
    }

    pause() {
      return this.requireHandle().pause();
    }

    resume() {
      return this.requireHandle().resume();
    }

    restart() {
      return this.requireHandle().restart();
    }

    setVolume(volume: number) {
      return this.requireHandle().setVolume(volume);
    }

    requestGameFullscreen() {
      return this.requireHandle().requestFullscreen();
    }

    private requireHandle(): RetroArcadeEmbedHandle {
      if (!this.handle) throw new Error('Retro Arcade custom element is not connected.');
      return this.handle;
    }

    private dispatchRetroEvent(name: string, detail: unknown) {
      this.dispatchEvent(
        new CustomEvent(`retro-arcade-${name}`, {
          detail,
          bubbles: false,
          composed: true,
        }),
      );
    }

    private reportError(error: unknown) {
      const detail = {
        code: 'CUSTOM_ELEMENT_ERROR',
        message: error instanceof Error ? error.message : 'Retro Arcade custom element failed.',
      };
      this.dispatchRetroEvent('error', detail);
    }

    private teardown() {
      for (const unsubscribe of this.unsubscribeEvents) unsubscribe();
      this.unsubscribeEvents = [];
      this.handle?.destroy();
      this.handle = null;
    }
  }

  registry.define(retroArcadePlayerTagName, RetroArcadePlayerElementImplementation);
}
